import { useState, useCallback } from 'react';

/**
 * A custom React hook to manage boolean state toggling.
 *
 * This hook provides a toggle function that can either:
 * - **Toggle the current state** (default behavior).
 * - **Explicitly set the state** (optional parameter).
 *
 * @param {boolean} [initialState=false] - The initial boolean state value (defaults to `false`).
 * @returns {[boolean, (state?: boolean) => void]} - Returns a tuple containing:
 *  - `state` (boolean) - The current boolean state.
 *  - `toggle` (function) - A function that:
 *    - **Toggles the state** if called without arguments.
 *    - **Explicitly sets the state** if passed `true` or `false`.
 *
 * @example
 * // Basic toggle functionality
 * const [isOpen, toggleOpen] = useToggle(false);
 *
 * return (
 *   <button onClick={toggleOpen}>
 *     {isOpen ? "Close" : "Open"}
 *   </button>
 * );
 *
 * @example
 * // Explicitly setting the state
 * const [isActive, toggleActive] = useToggle(false);
 *
 * return (
 *   <>
 *     <button onClick={() => toggleActive(true)}>Activate</button>
 *     <button onClick={() => toggleActive(false)}>Deactivate</button>
 *     <button onClick={toggleActive}>Toggle</button>
 *   </>
 * );
 */
export const useToggle = (initialState = false) => {
    const [state, setState] = useState(initialState);

    /**
     * Toggles the current state or sets it explicitly if a boolean value is provided.
     *
     * @param {boolean} [state] - Optional new state value.
     * If omitted, it toggles the current state.
     */
    const toggle = useCallback((state?: boolean) => setState(prev => state ?? !prev), []);

    return [state, toggle] as const;
};
